import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import EmptyState from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton, SkeletonTable } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { reportService } from "@/services/reportService";
import type { WeeklyReport } from "@/types/WeeklyReport";
import { isDraft } from "@/utils/statusResolution";
import { format, isValid } from "date-fns";
import { Eye, FileText, Filter, Search, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Helper function to safely format dates
const safeFormatDate = (
  dateValue: string | undefined | null,
  formatStr: string,
  fallback: string = "N/A",
): string => {
  if (!dateValue) return fallback;

  try {
    const date = new Date(dateValue);
    if (!isValid(date)) {
      return fallback;
    }
    return format(date, formatStr);
  } catch (error) {
    console.warn("Error formatting date:", dateValue, error);
    return fallback;
  }
};

const Reports = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState<WeeklyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<WeeklyReport | null>(
    null,
  );

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "sent">("all");
  const [clientFilter, setClientFilter] = useState<string>("all");

  useEffect(() => {
    if (user?.email) {
      loadReports();
    }
  }, [user]);

  const loadReports = async () => {
    try {
      setLoading(true);
      const userReports = await reportService.getReports(user?.email || "");

      // Normalize report task arrays
      const normalizedReports = userReports.map((report) => {
        const normalizeTaskArray = (
          tasks: any[] | undefined,
        ): { value: string; dependency?: string }[] => {
          if (!tasks) return [];
          return tasks
            .map((task) =>
              typeof task === "string"
                ? { value: task, dependency: "" }
                : {
                    value: task?.value || "",
                    dependency: task?.dependency || "",
                  },
            )
            .filter((task) => task.value?.trim());
        };
        return {
          ...report,
          last_week_tasks: normalizeTaskArray(report.last_week_tasks),
          Unplanned: normalizeTaskArray(report.Unplanned),
          blockers: normalizeTaskArray(report.blockers),
          next_week_tasks: normalizeTaskArray(report.next_week_tasks),
          further_beyond: normalizeTaskArray(report.further_beyond),
        };
      });

      // Sort normalized reports by date
      const sortedReports = [...normalizedReports].sort((a, b) => {
        const getDateValue = (report: WeeklyReport): number => {
          const dateStr = report.date || (report as any).createdAt;
          if (!dateStr) return 0;
          const date = new Date(dateStr);
          return isValid(date) ? date.getTime() : 0;
        };

        const dateA = getDateValue(a);
        const dateB = getDateValue(b);
        return dateB - dateA;
      });

      setReports(sortedReports);
    } catch (error) {
      console.error("Error loading reports:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get unique clients for filter
  const uniqueClients = useMemo(() => {
    const clients = new Set(
      reports.filter((r) => r.clientName).map((r) => r.clientName),
    );
    return Array.from(clients);
  }, [reports]);

  // Filter reports
  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSubject = report.subject?.toLowerCase().includes(query);
        const matchesClient = report.clientName?.toLowerCase().includes(query);
        if (!matchesSubject && !matchesClient) return false;
      }

      // Status filter
      if (statusFilter !== "all") {
        if (statusFilter === "sent" && !report.sentByEmail) return false;
      }

      // Client filter
      if (clientFilter !== "all" && report.clientName !== clientFilter) {
        return false;
      }

      return true;
    });
  }, [reports, searchQuery, statusFilter, clientFilter]);

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setClientFilter("all");
  };

  const hasActiveFilters =
    searchQuery || statusFilter !== "all" || clientFilter !== "all";

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">
            Reports
          </h1>
          <p className="text-muted-foreground mt-1">
            View and manage all your weekly reports
          </p>
        </div>
        <Button onClick={() => navigate("/dashboard?tab=create")}>
          Create Report
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search reports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Status Filter */}
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as any)}
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
              </SelectContent>
            </Select>

            {/* Client Filter */}
            {uniqueClients.length > 0 && (
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {uniqueClients.map((client) => (
                    <SelectItem key={client} value={client!}>
                      {client}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="ghost" size="icon" onClick={clearFilters}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Your Reports</CardTitle>
              <CardDescription>
                {filteredReports.length} of {reports.length} reports
              </CardDescription>
            </div>
            {hasActiveFilters && (
              <Badge variant="secondary" className="gap-1">
                <Filter className="h-3 w-3" />
                Filtered
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <SkeletonTable rows={5} />
          ) : filteredReports.length === 0 ? (
            <EmptyState
              icon={FileText}
              title={
                hasActiveFilters ? "No matching reports" : "No reports yet"
              }
              description={
                hasActiveFilters
                  ? "Try adjusting your filters to find what you're looking for."
                  : "Create your first weekly report to get started."
              }
              action={
                hasActiveFilters
                  ? {
                      label: "Clear Filters",
                      onClick: clearFilters,
                    }
                  : {
                      label: "Create Report",
                      onClick: () => navigate("/dashboard?tab=create"),
                    }
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Subject</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.map((report, index) => (
                  <TableRow
                    key={report.id}
                    className="animate-slide-up cursor-pointer"
                    style={{ animationDelay: `${index * 30}ms` }}
                    onClick={() => setSelectedReport(report)}
                  >
                    <TableCell className="font-medium">
                      {report.subject || "Untitled Report"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {safeFormatDate(
                        report.date || (report as any).createdAt,
                        "MMM d, yyyy",
                        "N/A",
                      )}
                    </TableCell>
                    <TableCell>
                      {report.clientName ? (
                        <Badge variant="secondary">{report.clientName}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={isDraft(report) ? "draft" : "sent"}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedReport(report);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Report Detail Dialog */}
      <Dialog
        open={!!selectedReport}
        onOpenChange={(open) => !open && setSelectedReport(null)}
      >
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  selectedReport?.sentByEmail
                    ? "bg-success/10 text-success"
                    : "bg-warning/10 text-warning"
                }`}
              >
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <span className="block">
                  {selectedReport?.subject || "Report Details"}
                </span>
                <span className="text-sm font-normal text-muted-foreground">
                  {safeFormatDate(
                    selectedReport?.date || (selectedReport as any)?.createdAt,
                    "MMMM d, yyyy",
                    "Date not available",
                  )}
                </span>
              </div>
            </DialogTitle>
            <DialogDescription className="sr-only">
              View details of the selected weekly report including tasks, blockers, and notes.
            </DialogDescription>
          </DialogHeader>

          {selectedReport && (
            <div className="space-y-6 mt-4">
              {/* Meta info */}
              <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-secondary/30">
                <div>
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    From
                  </span>
                  <p className="text-sm text-foreground mt-1">
                    {selectedReport.createdBy || "Unknown"}
                  </p>
                </div>
                <div>
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </span>
                  <div className="mt-1">
                    <StatusBadge
                      status={isDraft(selectedReport) ? "draft" : "sent"}
                    />
                  </div>
                </div>
                {selectedReport.clientName && (
                  <div className="col-span-2">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Client
                    </span>
                    <p className="text-sm text-foreground mt-1">
                      <Badge variant="secondary">
                        {selectedReport.clientName}
                      </Badge>
                    </p>
                  </div>
                )}
              </div>

              {/* Report Content Sections */}
              <div className="space-y-4">
                {selectedReport.last_week_tasks &&
                  selectedReport.last_week_tasks.length > 0 && (
                    <ReportSection
                      title="Last Week Tasks"
                      items={selectedReport.last_week_tasks}
                      color="cyan"
                    />
                  )}
                {selectedReport.Unplanned &&
                  selectedReport.Unplanned.length > 0 && (
                    <ReportSection
                      title="Unplanned"
                      items={selectedReport.Unplanned}
                      color="amber"
                    />
                  )}
                {selectedReport.blockers &&
                  selectedReport.blockers.length > 0 && (
                    <ReportSection
                      title="Blockers"
                      items={selectedReport.blockers}
                      color="red"
                    />
                  )}
                {selectedReport.next_week_tasks &&
                  selectedReport.next_week_tasks.length > 0 && (
                    <ReportSection
                      title="Next Week Tasks"
                      items={selectedReport.next_week_tasks}
                      color="emerald"
                    />
                  )}
                {selectedReport.further_beyond &&
                  selectedReport.further_beyond.length > 0 && (
                    <ReportSection
                      title="Further Beyond"
                      items={selectedReport.further_beyond}
                      color="purple"
                    />
                  )}
                {selectedReport.notes && (
                  <div className="p-4 rounded-lg border border-border">
                    <h4 className="text-sm font-semibold text-foreground mb-2">
                      Notes
                    </h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {selectedReport.notes}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => setSelectedReport(null)}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setSelectedReport(null);
                    navigate(`/dashboard?tab=create&edit=${selectedReport.id}`);
                  }}
                >
                  Edit Report
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Helper component for report sections
const ReportSection = ({
  title,
  items,
  color,
}: {
  title: string;
  items: { value: string; dependency?: string }[];
  color: "cyan" | "amber" | "red" | "emerald" | "purple";
}) => {
  const colorClasses = {
    cyan: "border-l-cyan-400",
    amber: "border-l-amber-400",
    red: "border-l-red-400",
    emerald: "border-l-emerald-400",
    purple: "border-l-purple-400",
  };

  return (
    <div
      className={`p-4 rounded-lg border border-border border-l-4 ${colorClasses[color]}`}
    >
      <h4 className="text-sm font-semibold text-foreground mb-3">{title}</h4>
      <ul className="space-y-2">
        {items
          .filter((item) => item?.value?.trim())
          .map((item, index) => (
            <li key={index} className="flex items-start gap-2 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
              <div>
                <span className="text-foreground">{item.value}</span>
                {item.dependency && (
                  <span className="text-xs text-muted-foreground ml-2">
                    (Dep: {item.dependency})
                  </span>
                )}
              </div>
            </li>
          ))}
      </ul>
    </div>
  );
};

export default Reports;
